import React from 'react';
import { Link } from 'react-router-dom';
import {
  Printer, ArrowLeft, Landmark, Shield, LogIn, KeyRound, Send,
  ListChecks, MapPin, MessageSquare, Megaphone, Globe, QrCode,
  Phone, Mail, Pin, X as XIcon, Timer, Users, ShieldCheck,
  BellRing, AlertTriangle, CheckCircle2, ArrowDown, Info, Radio,
  Building2, Zap, Droplets, Route, HeartPulse, Plus, CalendarClock,
} from 'lucide-react';

/**
 * Aug 2026 — Official Authority Onboarding Guide (v2).
 *
 * Full rewrite from the "Community Safety Hub" v1: broader "Community
 * Communication Platform" positioning, expanded to 18 sections, printable
 * cleanly on A4 (multi-page allowed via `break-inside: avoid`), tested on
 * desktop/tablet/mobile.
 *
 * All copy has been checked against the live production routes and button
 * names:
 *   - Publish flow: authorities start on the Profile page. The
 *     `AuthorityMiniDashboard` card at the top of Profile carries the
 *     primary "+ Publish Safety Alert" button (which deep-links to
 *     /safety-bulletin/submit) and a secondary "My Scheduled Alerts"
 *     button (deep-links to /authority/scheduled). The old
 *     "Community Hub → Safety & Community → Submit Safety Alert" path
 *     is retired and no longer referenced in the guide.
 *   - Supported languages: EN, ES, DE, NL, FR, AR (LanguageToggle.jsx).
 *   - Rate limits (residents 3/day, officials 10/day) preserved from v1.
 *   - Notification channels reflect the actual v6.1 fan-out: push, in-app
 *     Notification Bell, badge, and email backup for verified residents.
 *   - No promise of automatic notice translation (not implemented).
 *
 * Editable via a small `CONFIG` constant at the top of this file — admins can
 * change the help email, supported-language list, QR destination, footer and
 * version date without touching the JSX.
 */

// Admin-editable configuration. Update the values here and the guide reflects
// them instantly. Kept as a plain module constant to keep the printable HTML
// simple; can be swapped for a backend-driven config later without changing
// the layout below.
const CONFIG = {
  version: 'August 2026',
  helpEmail: 'support@find-2-day.com',
  websiteUrl: 'find-2-day.com',
  qrDestination: 'https://find-2-day.com',
  qrLabel: 'SCAN TO OPEN FIND-2-DAY',
  supportedLanguages: ['English', 'Spanish', 'German', 'Dutch', 'French', 'Arabic'],
  supportSlaText: 'We aim to respond within one working day.',
  footerLine1: 'Find-2-Day · Almería Local Community Platform',
  footerLine2: 'Official Authority accounts are provided free of charge.',
};

// Small primitive used throughout for coloured section headings; keeps the
// visual hierarchy consistent between the 18 sections.
const SectionHeading = ({ icon: Icon, tint = 'blue', number, children }) => {
  const tints = {
    blue: 'text-blue-900 border-blue-200 bg-blue-50/60',
    orange: 'text-orange-800 border-orange-200 bg-orange-50/60',
    emerald: 'text-emerald-800 border-emerald-200 bg-emerald-50/60',
    red: 'text-red-800 border-red-200 bg-red-50/60',
    slate: 'text-slate-800 border-slate-200 bg-slate-50/60',
  };
  return (
    <div className={`flex items-center gap-2 rounded-md border px-3 py-2 mb-3 print:mb-2 ${tints[tint]}`}>
      {number != null && (
        <span className="text-[11px] font-bold tracking-widest opacity-70">{String(number).padStart(2, '0')}</span>
      )}
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      <h2 className="text-[13px] font-extrabold uppercase tracking-wide">{children}</h2>
    </div>
  );
};

// Simple horizontal step visual used for the two process diagrams. Rendered as
// a clean stack on print / mobile.
const FlowStep = ({ label, tint = 'blue' }) => {
  const map = {
    blue: 'bg-blue-50 border-blue-300 text-blue-900',
    orange: 'bg-orange-50 border-orange-300 text-orange-900',
    emerald: 'bg-emerald-50 border-emerald-300 text-emerald-900',
    slate: 'bg-slate-50 border-slate-300 text-slate-900',
  };
  return (
    <div className={`flex-1 min-w-[140px] rounded-lg border-2 ${map[tint]} px-3 py-2 text-[12px] font-semibold text-center`}>
      {label}
    </div>
  );
};

const FlowArrow = () => (
  <div className="flex items-center justify-center py-1 print:py-0.5">
    <ArrowDown className="h-4 w-4 text-slate-400 shrink-0" />
  </div>
);

// Small badge for the top ribbon.
const TopBadge = ({ children }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-orange-600 text-white text-[10px] font-bold uppercase tracking-widest">
    {children}
  </span>
);

const AuthorityOnboardingGuide = () => {
  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-slate-100 py-6 print:bg-white print:py-0" data-testid="authority-onboarding-page">
      {/* Toolbar — hidden on print */}
      <div className="max-w-4xl mx-auto px-4 mb-4 flex items-center justify-between print:hidden">
        <Link
          to="/admin/dashboard"
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
          data-testid="guide-back-link"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Admin
        </Link>
        <button
          type="button"
          onClick={handlePrint}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold shadow-sm"
          data-testid="guide-print-btn"
        >
          <Printer className="h-4 w-4" /> Print / Save as PDF
        </button>
      </div>

      {/* Printable sheet */}
      <article
        className="max-w-4xl mx-auto bg-white print:max-w-none print:shadow-none shadow-sm border border-slate-200 print:border-0 rounded-xl print:rounded-none p-6 sm:p-8 print:p-0 leading-normal"
        data-testid="authority-onboarding-guide"
      >
        {/* ============== HEADER RIBBON ============== */}
        <header className="mb-6 print:mb-4">
          <div className="flex items-center justify-between mb-3">
            <TopBadge>Find-2-Day · Official Authority Onboarding</TopBadge>
            <span className="text-[10px] text-slate-500 font-mono" data-testid="guide-version">
              Guide version: {CONFIG.version}
            </span>
          </div>
          <div className="border-b-4 border-blue-900 pb-4">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex items-center gap-2 shrink-0">
                <Landmark className="h-8 w-8 text-blue-900" />
                <Shield className="h-8 w-8 text-orange-600" />
              </div>
              <div className="flex-1">
                <h1 className="text-[22px] sm:text-3xl font-extrabold text-blue-900 leading-tight tracking-tight">
                  Welcome to the Find-2-Day Community Communication Platform
                </h1>
                <p className="text-sm text-slate-700 mt-1">
                  Official Guide for Town Halls, Ayuntamientos, Police, Guardia Civil
                  and Verified Authorities.
                </p>
                <p className="text-[13px] font-bold text-emerald-700 mt-1.5" data-testid="guide-free-forever">
                  Official Authority accounts are free of charge — forever.
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* ============== 1. INTRODUCTION ============== */}
        <section className="mb-5 print:mb-4 print:break-inside-avoid">
          <SectionHeading tint="blue" number={1} icon={Radio}>
            Your direct line to your community
          </SectionHeading>
          <p className="text-[13px] text-slate-800 leading-relaxed">
            Find-2-Day allows verified official authorities to communicate directly with
            residents who choose to follow their town.
          </p>
          <p className="text-[13px] text-slate-800 leading-relaxed mt-2">
            Authorities can quickly publish trusted and factual information including
            safety alerts, service interruptions, road closures, public notices,
            community updates, events, emergency information and important local
            announcements.
          </p>
          <p className="text-[13px] text-slate-800 leading-relaxed mt-2">
            Every verified authority account is clearly marked <strong>OFFICIAL</strong>,
            helping residents distinguish trusted information from rumours or unverified
            social-media posts.
          </p>
        </section>

        {/* ============== 2 & 3. LOGIN + CHANGE PASSWORD ============== */}
        <section className="mb-5 print:mb-4 grid grid-cols-1 md:grid-cols-2 gap-4 print:break-inside-avoid">
          <div>
            <SectionHeading tint="blue" number={2} icon={LogIn}>
              How to log in
            </SectionHeading>
            <ol className="text-[12.5px] text-slate-800 space-y-1 list-decimal list-inside leading-snug">
              <li>Open <strong>{CONFIG.websiteUrl}</strong> in a browser or open the Find-2-Day app.</li>
              <li>Tap <strong>Sign in</strong> at the top right.</li>
              <li>Enter the email address and temporary password supplied by the administrator.</li>
              <li>After signing in, open <strong>Profile</strong> from the menu.</li>
            </ol>
          </div>
          <div>
            <SectionHeading tint="blue" number={3} icon={KeyRound}>
              Change your password
            </SectionHeading>
            <ol className="text-[12.5px] text-slate-800 space-y-1 list-decimal list-inside leading-snug">
              <li>After signing in, open <strong>Profile</strong>.</li>
              <li>Select <strong>Change Password</strong>.</li>
              <li>Create a secure password containing at least 8 characters.</li>
              <li>Store the password securely and do not share the account with unauthorised people.</li>
            </ol>
          </div>
        </section>

        {/* ============== 4. HOW TO PUBLISH AN OFFICIAL NOTICE ============== */}
        <section className="mb-5 print:mb-4 print:break-inside-avoid">
          <SectionHeading tint="orange" number={4} icon={Send}>
            How to publish an Official Notice
          </SectionHeading>

          {/* Aug 2026 — updated to reflect the current Town Hall workflow.
              The publish CTA now lives on the Profile page under the
              "Official Safety Source · TOWN HALL" card (rendered by
              AuthorityMiniDashboard for verified authorities). The old
              Community Hub → Safety & Community → Submit path is gone. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
            {/* Left: the actual sign-in → profile → card flow */}
            <div className="md:col-span-2">
              <ol className="text-[12.5px] text-slate-800 space-y-1 list-decimal list-inside leading-snug">
                <li>Tap <strong>Sign in</strong> and enter your authority email + password.</li>
                <li>Open <strong>Profile</strong> from the menu.</li>
                <li>
                  At the top of the Profile page you will see an amber card headed{' '}
                  <strong>Official Safety Source · TOWN HALL</strong> showing your
                  display name, locked town and current follower count.
                </li>
                <li>
                  Tap the primary orange button <strong>+ Publish Safety Alert</strong>.
                </li>
                <li>Your assigned town is already selected — you don&apos;t need to choose it.</li>
                <li>Choose the most appropriate category (Factual Incident, Safety Tip, Lost Pet, or Community Alert).</li>
                <li>Enter a short, clear and factual title (up to 80 characters).</li>
                <li>Add the full notice, including relevant dates, times, locations and instructions (up to 500 characters).</li>
                <li>Where appropriate, set an expiry date/time so time-sensitive notices disappear automatically.</li>
                <li>Review the information carefully.</li>
                <li>Tick the confirmation declaration and confirm the publish action.</li>
                <li>The notice becomes available immediately to eligible residents following your town.</li>
              </ol>
              <p className="text-[11px] text-slate-500 mt-2 italic">
                Rate limit: verified official accounts can publish up to 10 notices per 24 hours
                (residents are capped at 3).
              </p>
            </div>

            {/* Right: mock of the exact card the user will see on Profile */}
            <div
              className="rounded-lg border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-amber-50 to-orange-50 p-3 print:break-inside-avoid"
              aria-label="Mock of the Official Safety Source card on the Profile page"
              data-testid="guide-profile-card-mock"
            >
              <div className="flex items-start gap-2">
                <div className="h-9 w-9 rounded-full bg-amber-600 text-white flex items-center justify-center shrink-0">
                  <Landmark className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-amber-700 leading-tight">
                    Official Safety Source · TOWN HALL
                  </p>
                  <p className="text-[13px] font-extrabold text-slate-900 leading-tight mt-0.5">
                    Ayuntamiento de {`{Your Town}`}
                  </p>
                  <p className="text-[11px] text-slate-700 leading-tight mt-0.5">
                    <strong>{`{Your Town}`}</strong>
                    <span className="mx-1 text-slate-300">·</span>
                    128 residents following
                  </p>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                <div className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-amber-600 text-white text-[11px] font-semibold">
                  <Plus className="h-3 w-3" /> Publish Safety Alert
                </div>
                <div className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-white border border-amber-300 text-amber-800 text-[11px] font-semibold">
                  <CalendarClock className="h-3 w-3" /> My Scheduled Alerts
                </div>
              </div>
              <p className="text-[9px] text-amber-800/80 mt-2 leading-tight italic">
                This is what the top of your Profile page looks like once your account
                is verified.
              </p>
            </div>
          </div>

          {/* Workflow diagram (updated to match reality) */}
          <div className="rounded-lg border border-orange-200 bg-orange-50/40 p-3 print:bg-white print:break-inside-avoid">
            <p className="text-[10px] uppercase tracking-widest text-orange-800 font-bold mb-2">Publish workflow</p>
            <div className="flex flex-col items-center gap-0">
              <FlowStep label="Sign In" tint="blue" />
              <FlowArrow />
              <FlowStep label="Open Profile" tint="blue" />
              <FlowArrow />
              <FlowStep label="Official Safety Source — Town Hall (card at top of Profile)" tint="orange" />
              <FlowArrow />
              <FlowStep label="+ Publish Safety Alert" tint="orange" />
              <FlowArrow />
              <FlowStep label="Category · Title · Details · Expiry" tint="slate" />
              <FlowArrow />
              <FlowStep label="Confirm declaration → publish" tint="emerald" />
            </div>
          </div>

          <p className="text-[11px] text-slate-500 mt-3 italic">
            Tip: the secondary <strong>My Scheduled Alerts</strong> button on the same
            card lets you review, edit or cancel any alerts you&apos;ve queued for a
            future date and time.
          </p>
        </section>

        {/* ============== 5. HOW RESIDENTS RECEIVE MESSAGES ============== */}
        <section className="mb-5 print:mb-4 print:break-inside-avoid">
          <SectionHeading tint="blue" number={5} icon={BellRing}>
            How residents receive your messages
          </SectionHeading>

          {/* Flow diagram */}
          <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 print:bg-white">
            <div className="flex flex-col items-center gap-0">
              <FlowStep label="Official Authority" tint="blue" />
              <FlowArrow />
              <FlowStep label="Publishes an Official Notice" tint="orange" />
              <FlowArrow />
              <FlowStep label="Find-2-Day" tint="slate" />
              <FlowArrow />
              <FlowStep label="Residents following the relevant town" tint="emerald" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 text-[12.5px] text-slate-800">
            <div className="flex items-start gap-2"><BellRing className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" /> Push notification</div>
            <div className="flex items-start gap-2"><Info className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" /> In-app Notification Bell</div>
            <div className="flex items-start gap-2"><Radio className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" /> Notification badge on the app icon</div>
            <div className="flex items-start gap-2"><Mail className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" /> Email, where enabled and applicable</div>
            <div className="flex items-start gap-2 sm:col-span-2"><MapPin className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" /> Official notice card within the relevant app area (Community Hub)</div>
          </div>

          <p className="text-[12px] text-slate-700 mt-3 leading-snug">
            <strong>Residents receive official notices according to the towns they choose to
            follow and their enabled notification preferences.</strong> Delivery may depend on the
            notice category, the resident&apos;s device permissions and whether they have a valid
            registered email.
          </p>
        </section>

        {/* ============== 6. OFFICIAL AND TRUSTED ============== */}
        <section className="mb-5 print:mb-4 print:break-inside-avoid">
          <SectionHeading tint="emerald" number={6} icon={ShieldCheck}>
            Official and Trusted
          </SectionHeading>
          <p className="text-[13px] text-slate-800 leading-relaxed mb-2">
            Verified authority accounts display an <strong>OFFICIAL</strong> status so
            residents can immediately recognise that the information comes from an approved
            organisation.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-[12px] text-slate-800">
            <div className="flex items-center gap-1.5"><Landmark className="h-3.5 w-3.5 text-emerald-700" /> Town Hall / Ayuntamiento</div>
            <div className="flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-emerald-700" /> Policía Local</div>
            <div className="flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-emerald-700" /> Guardia Civil</div>
            <div className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-700" /> Civil Protection</div>
            <div className="flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-emerald-700" /> Fire &amp; Emergency Services</div>
            <div className="flex items-center gap-1.5"><Droplets className="h-3.5 w-3.5 text-emerald-700" /> Water service providers</div>
            <div className="flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-emerald-700" /> Electricity providers</div>
            <div className="flex items-center gap-1.5"><HeartPulse className="h-3.5 w-3.5 text-emerald-700" /> Health authorities</div>
            <div className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-emerald-700" /> Other approved public bodies</div>
          </div>
          <p className="text-[11px] text-slate-500 mt-2 italic">
            Account approval and official status are controlled by the Find-2-Day
            administration team.
          </p>
        </section>

        {/* ============== 7. WHAT CAN YOU POST? ============== */}
        <section className="mb-5 print:mb-4 print:break-inside-avoid">
          <SectionHeading tint="orange" number={7} icon={ListChecks}>
            What can you post?
          </SectionHeading>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              {
                title: 'Community Safety',
                icon: Shield,
                items: [
                  'Severe-weather warnings',
                  'Fire or flood information',
                  'Verified scam or fraud warnings',
                  'Missing-person notices approved for public release',
                  'Public-safety incidents',
                  'Follow-up safety updates',
                ],
              },
              {
                title: 'Public Services',
                icon: Route,
                items: [
                  'Water interruptions',
                  'Electricity outages',
                  'Road closures and diversions',
                  'Public-transport changes',
                  'Waste or recycling collection changes',
                  'Temporary service disruptions',
                ],
              },
              {
                title: 'Community Information',
                icon: Users,
                items: [
                  'Council notices',
                  'Public meetings',
                  'Festivals and official events',
                  'Community initiatives',
                  'Sporting or cultural activities',
                  'Important public announcements',
                ],
              },
              {
                title: 'Emergency Updates',
                icon: AlertTriangle,
                items: [
                  'Evacuation information',
                  'Incident updates',
                  'Road reopenings',
                  'Service restoration updates',
                  'All-clear messages',
                  'Changes to emergency advice',
                ],
              },
            ].map(({ title, icon: Icon, items }) => (
              <div key={title} className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 print:bg-white print:break-inside-avoid">
                <h3 className="flex items-center gap-2 text-[13px] font-bold text-emerald-800 mb-1.5">
                  <Icon className="h-4 w-4" /> {title}
                </h3>
                <ul className="text-[12px] text-slate-800 space-y-0.5 list-disc list-inside leading-snug">
                  {items.map((it) => <li key={it}>{it}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* ============== 8. DO NOT POST ============== */}
        <section className="mb-5 print:mb-4 print:break-inside-avoid">
          <SectionHeading tint="red" number={8} icon={XIcon}>
            Do not post
          </SectionHeading>
          <div className="rounded-lg border border-red-200 bg-red-50/40 p-3 print:bg-white">
            <ul className="text-[12.5px] text-slate-800 space-y-0.5 list-disc list-inside leading-snug">
              <li>Personal information that is not authorised for public release</li>
              <li>Names, addresses, licence plates or identifying details of private individuals unless legally authorised</li>
              <li>Accusations or unverified allegations</li>
              <li>Graphic photographs or videos</li>
              <li>Political campaigning or party-political material</li>
              <li>Commercial advertising or private promotions</li>
              <li>Confidential operational information</li>
              <li>Information that could interfere with an active investigation</li>
              <li>Content that could put a person at risk</li>
              <li>Misleading, speculative or unverified information</li>
              <li>Content that should instead be handled through emergency services</li>
            </ul>
          </div>
          <div className="mt-2 flex items-start gap-2 rounded-md border-2 border-red-500 bg-red-50 px-3 py-2 print:bg-white print:break-inside-avoid" data-testid="guide-112-warning">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <p className="text-[12.5px] text-red-900 font-bold">
              Find-2-Day is not a replacement for emergency services. In an immediate emergency, contact <span className="font-mono">112</span>.
            </p>
          </div>
        </section>

        {/* ============== 9. WRITING A CLEAR OFFICIAL NOTICE ============== */}
        <section className="mb-5 print:mb-4 print:break-inside-avoid">
          <SectionHeading tint="blue" number={9} icon={CheckCircle2}>
            Writing a clear Official Notice
          </SectionHeading>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ul className="text-[12.5px] text-slate-800 space-y-1 list-disc list-inside leading-snug">
              <li>Use a short and factual title.</li>
              <li>State exactly what has happened.</li>
              <li>Give the affected town, road or area.</li>
              <li>Include the date and time.</li>
              <li>Explain what residents should do.</li>
              <li>State when the next update is expected.</li>
              <li>Update or withdraw the notice when circumstances change.</li>
              <li>Avoid rumours, speculation and unnecessary personal details.</li>
              <li>Check names, dates, locations and contact details before publishing.</li>
            </ul>
            <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 print:bg-white">
              <p className="text-[11px] uppercase tracking-wider font-bold text-blue-800">Example</p>
              <p className="text-[13px] font-bold text-slate-900 mt-1">Temporary Water Interruption — Calle Mayor</p>
              <p className="text-[12.5px] text-slate-800 leading-snug mt-1">
                Water service on Calle Mayor will be temporarily interrupted on Tuesday from
                09:00 until approximately 13:00 while essential repairs are completed.
                Residents are advised to store sufficient water in advance. A further
                update will be published if the work takes longer than expected.
              </p>
            </div>
          </div>
        </section>

        {/* ============== 10. SMART FEATURES FOR RESIDENTS ============== */}
        <section className="mb-5 print:mb-4 print:break-inside-avoid">
          <SectionHeading tint="slate" number={10} icon={BellRing}>
            Smart features for residents
          </SectionHeading>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-lg border border-slate-200 p-3 print:break-inside-avoid">
              <h3 className="flex items-center gap-2 text-[13px] font-bold text-slate-900">
                <Pin className="h-4 w-4 text-orange-600" /> Pin important notices
              </h3>
              <p className="text-[12px] text-slate-700 mt-1 leading-snug">
                Residents can pin important notifications so they remain easy to find in
                their bell.
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 print:break-inside-avoid">
              <h3 className="flex items-center gap-2 text-[13px] font-bold text-slate-900">
                <XIcon className="h-4 w-4 text-slate-600" /> Dismiss notifications
              </h3>
              <p className="text-[12px] text-slate-700 mt-1 leading-snug">
                Residents can dismiss notifications they no longer need. On supported
                devices, they may also swipe left to dismiss.
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 print:break-inside-avoid">
              <h3 className="flex items-center gap-2 text-[13px] font-bold text-slate-900">
                <Timer className="h-4 w-4 text-orange-600" /> Automatic expiry
              </h3>
              <p className="text-[12px] text-slate-700 mt-1 leading-snug">
                Time-sensitive notices may automatically expire when their configured
                expiry time is reached, keeping information relevant.
              </p>
            </div>
          </div>
        </section>

        {/* ============== 11. HOW RESIDENTS FOLLOW A TOWN ============== */}
        <section className="mb-5 print:mb-4 print:break-inside-avoid">
          <SectionHeading tint="blue" number={11} icon={MapPin}>
            How residents follow your town
          </SectionHeading>
          <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 print:bg-white">
            <div className="flex flex-col items-center gap-0">
              <FlowStep label="Choose Town" tint="blue" />
              <FlowArrow />
              <FlowStep label="Follow Town" tint="orange" />
              <FlowArrow />
              <FlowStep label="Receive eligible official notifications" tint="emerald" />
              <FlowArrow />
              <FlowStep label="Change or unfollow at any time" tint="slate" />
            </div>
          </div>
          <p className="text-[12.5px] text-slate-800 leading-snug mt-2">
            Residents control which towns they follow. Authorities should encourage
            residents to install Find-2-Day, create an account where required, and follow
            the correct town.
          </p>
        </section>

        {/* ============== 12. HELP RESIDENTS FIND YOUR TOWN ============== */}
        <section className="mb-5 print:mb-4 print:break-inside-avoid">
          <SectionHeading tint="orange" number={12} icon={Megaphone}>
            Help residents follow your town
          </SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <ul className="text-[12.5px] text-slate-800 space-y-1 list-disc list-inside leading-snug">
                <li>Add Find-2-Day to the Town Hall website.</li>
                <li>Share the app through official social-media accounts.</li>
                <li>Include it in newsletters and public bulletins.</li>
                <li>Display posters in Town Hall buildings.</li>
                <li>Display information at police, health and community centres.</li>
                <li>Use printed hand-outs at community events.</li>
                <li>Add QR codes linking residents to Find-2-Day.</li>
                <li>Include instructions showing residents how to follow the town.</li>
              </ul>
              <p className="text-[11px] text-slate-500 mt-2 italic">
                We can supply branded QR-code posters for your town on request.
              </p>
            </div>
            <div className="rounded-lg border-2 border-orange-300 p-3 flex flex-col items-center justify-center bg-orange-50/40 print:bg-white print:break-inside-avoid">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=4&data=${encodeURIComponent(CONFIG.qrDestination)}`}
                alt={`QR code linking to ${CONFIG.qrDestination}`}
                className="w-32 h-32 rounded"
                data-testid="guide-qr"
              />
              <p className="text-[11px] text-orange-900 font-extrabold tracking-widest uppercase mt-2 text-center">
                {CONFIG.qrLabel}
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">{CONFIG.qrDestination}</p>
            </div>
          </div>
        </section>

        {/* ============== 13. MORE THAN SAFETY ALERTS ============== */}
        <section className="mb-5 print:mb-4 print:break-inside-avoid">
          <SectionHeading tint="blue" number={13} icon={ListChecks}>
            More than safety alerts
          </SectionHeading>
          <p className="text-[13px] text-slate-800 leading-relaxed mb-2">
            Find-2-Day is a local community platform designed to give residents useful
            reasons to return regularly.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12px] text-slate-800">
            {['Official Authority Notices', 'Community Updates', 'Events', 'Offers',
              'What\'s Open Today', 'Business Directory', 'Tourism & Local Information',
              'Community Hub'].map((label) => (
              <div key={label} className="rounded-md border border-slate-200 px-2 py-1.5 text-center leading-snug">
                {label}
              </div>
            ))}
          </div>
        </section>

        {/* ============== 14. MULTI-LANGUAGE SUPPORT ============== */}
        <section className="mb-5 print:mb-4 print:break-inside-avoid">
          <SectionHeading tint="slate" number={14} icon={Globe}>
            Multi-language support
          </SectionHeading>
          <p className="text-[13px] text-slate-800 leading-relaxed mb-2">
            Find-2-Day supports multiple interface languages, helping residents from
            different nationalities use the platform more easily.
          </p>
          <div className="flex flex-wrap gap-2 text-[12px] text-slate-800">
            {CONFIG.supportedLanguages.map((l) => (
              <span key={l} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-300 bg-slate-50 print:bg-white">
                <Globe className="h-3 w-3 text-slate-500" /> {l}
              </span>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-2 italic">
            The interface adapts to the resident&apos;s chosen language. Official notice text
            itself is not automatically translated — publish in the language that best
            reaches your residents.
          </p>
        </section>

        {/* ============== 15 & 16. WHY RESIDENTS / WHY AUTHORITIES ============== */}
        <section className="mb-5 print:mb-4 grid grid-cols-1 md:grid-cols-2 gap-4 print:break-inside-avoid">
          <div>
            <SectionHeading tint="emerald" number={15} icon={Users}>
              Why residents use Find-2-Day
            </SectionHeading>
            <ul className="text-[12.5px] text-slate-800 space-y-1 list-disc list-inside leading-snug">
              <li>Trusted official information</li>
              <li>Faster access to local updates</li>
              <li>Less reliance on social-media rumours</li>
              <li>One place for local information</li>
              <li>Control over which towns they follow</li>
              <li>Easy access to events, offers and local services</li>
              <li>Free access for residents</li>
            </ul>
          </div>
          <div>
            <SectionHeading tint="orange" number={16} icon={ShieldCheck}>
              Why authorities choose Find-2-Day
            </SectionHeading>
            <ul className="text-[12.5px] text-slate-800 space-y-1 list-disc list-inside leading-snug">
              <li>Verified official accounts</li>
              <li>Direct communication with local residents</li>
              <li>Town-based audience selection</li>
              <li>Push, in-app and eligible email delivery</li>
              <li>Clear official branding</li>
              <li>No dependence on social-media algorithms</li>
              <li>Multi-language interface</li>
              <li>Time-sensitive notice expiry</li>
              <li>Official Authority accounts provided free of charge</li>
            </ul>
          </div>
        </section>

        {/* ============== 17. HELP AND SUPPORT ============== */}
        <section className="mb-5 print:mb-4 print:break-inside-avoid">
          <SectionHeading tint="blue" number={17} icon={Phone}>
            Help and support
          </SectionHeading>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-slate-200 p-3">
              <h3 className="text-[12px] uppercase tracking-wider font-bold text-slate-600">Email support</h3>
              <p className="text-[13px] font-mono text-blue-800 mt-1 break-all">
                <a href={`mailto:${CONFIG.helpEmail}`} className="underline">{CONFIG.helpEmail}</a>
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <h3 className="text-[12px] uppercase tracking-wider font-bold text-slate-600">Password reset</h3>
              <p className="text-[12.5px] text-slate-800 mt-1 leading-snug">
                Use the <strong>Forgot Password</strong> option on the sign-in page.
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <h3 className="text-[12px] uppercase tracking-wider font-bold text-slate-600">Administrator assistance</h3>
              <p className="text-[12.5px] text-slate-800 mt-1 leading-snug">
                Contact the Find-2-Day administration team if you need help with account
                access, official verification, your assigned town or publishing permissions.
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <h3 className="text-[12px] uppercase tracking-wider font-bold text-slate-600">Account recovery</h3>
              <p className="text-[12.5px] text-slate-800 mt-1 leading-snug">
                Contact support if you no longer have access to the registered email address.
              </p>
            </div>
          </div>
          <p className="text-[12px] text-slate-600 italic mt-2">{CONFIG.supportSlaText}</p>
        </section>

        {/* ============== 18. FOOTER ============== */}
        <footer className="border-t-2 border-blue-900 pt-3 mt-4 print:break-inside-avoid" data-testid="guide-footer">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-2">
            <div>
              <p className="text-[12px] font-bold text-blue-900">{CONFIG.footerLine1}</p>
              <p className="text-[11px] text-emerald-700 font-semibold">{CONFIG.footerLine2}</p>
              <p className="text-[10px] text-slate-400 mt-1">© {new Date().getFullYear()} Find-2-Day. All rights reserved.</p>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-[11px] text-slate-600">
                <Globe className="inline h-3 w-3 mr-1 align-[-1px]" />
                <a href={`https://${CONFIG.websiteUrl}`} className="underline">{CONFIG.websiteUrl}</a>
              </p>
              <p className="text-[11px] text-slate-600">
                <Mail className="inline h-3 w-3 mr-1 align-[-1px]" />
                <a href={`mailto:${CONFIG.helpEmail}`} className="underline">{CONFIG.helpEmail}</a>
              </p>
              <p className="text-[10px] text-slate-400 mt-1">Guide version: {CONFIG.version}</p>
            </div>
          </div>
        </footer>
      </article>

      {/* Print tuning — allow multi-page layout with sensible page breaks. */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          html, body { background: #fff !important; }
          .print\\:hidden { display: none !important; }
          .print\\:break-inside-avoid { break-inside: avoid; page-break-inside: avoid; }
          article { box-shadow: none !important; }
          section { page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
};

export default AuthorityOnboardingGuide;
